import * as React from "react";

import { faAngleLeft } from "@fortawesome/free-solid-svg-icons";
import { Divider, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";

import { FontAwesomeIconWithTheme } from "src/common/components/ui/IconWrapper";
import { schedulerDisplayName } from "src/common/textAlteration";
import * as Tags from "src/lib/tags";

const Item = styled("div")(({ theme }) => ({
  ...theme.typography.body2,
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
  wordWrap: "break-word",
}));

const Info = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const BackButton = styled(Button)(() => ({
  bottom: "20px",
  position: "absolute",
}));

interface WorkflowInfoProps {
  workflow: Workflow;
  disk: {
    size: number;
    name: string;
  };
  cb: () => void;
}

const Field = ({ name, value }) => {
  return (
    <Item>
      <strong>{name}: </strong>
      {value}
    </Item>
  );
};

export const WorkflowMoreInfo: React.FC<WorkflowInfoProps> = ({ workflow, disk, cb }) => {
  const workflowDisplayName = workflow.tags?.[Tags.workflowDisplayName] || workflow.name;

  return (
    <>
      <Grid container spacing={8}>
        <Grid item xs={8}>
          <Typography variant="h6" color="textPrimary">
            Workflow Information
          </Typography>
          <Divider />
          <Info>
            <Field name="Name" value={workflowDisplayName} />
            <Field name="CloudFormation Name" value={workflow.name} />
            <Field name="Created" value={workflow.created_at} />
            <Field name="Status" value={workflow.status} />
            <Field name="Username" value={workflow?.tags?.[Tags.workflowUsername]} />
            <Field name="Workflow Blueprint Version" value={workflow?.tags?.[Tags.blueprintName]} />
            {workflow?.tags?.[Tags.templateName] && (
              <Field name="Template" value={workflow?.tags?.[Tags.templateName]} />
            )}
            <Field name="Job Scheduler" value={schedulerDisplayName(workflow?.scheduler)} />
            <Field name="Hostname" value={workflow?.tags?.[Tags.dnsName]} />
          </Info>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="h6" color="textPrimary">
            Parameters
          </Typography>
          <Divider />
          <Info>
            <Field name="Max # Nodes" value={workflow.max_queue_size} />
            <Field name="Initial # Nodes" value={workflow.initial_queue_size} />
            <Field name="Node Type" value={workflow?.tags?.[Tags.gpu] ? "GPU" : "CPU"} />
            <Field name="Disk" value={disk?.name} />
            <Field name="Disk Size" value={disk?.size + " GB"} />
            <Field name="Shared Dir" value="/data" />
            <Field name="Access From" value={workflow.access_from} />
            <Field name="Maintain Initial Size" value={workflow?.maintain_initial_size ? "Yes" : "No"} />
          </Info>
        </Grid>
      </Grid>
      <BackButton
        variant="contained"
        color="primary"
        size="small"
        onClick={cb}
        data-testid={workflowDisplayName + "-back"}
      >
        <FontAwesomeIconWithTheme icon={faAngleLeft} />
        Back
      </BackButton>
    </>
  );
};
